import React, { useEffect } from 'react';
import { XIcon, SparklesIcon, SendIcon, FilterIcon, MessageSquareIcon, PlusIcon } from 'lucide-react';
import { Tooltip } from '../Tooltip';
import type { AiMessage, AiConversation } from './types';
interface AiDrawerProps {
  aiMessages: AiMessage[];
  aiHistory: AiConversation[];
  aiInput: string;
  setAiInput: (v: string) => void;
  aiLoading: boolean;
  aiTab: 'chat' | 'history';
  setAiTab: (tab: 'chat' | 'history') => void;
  aiMessagesEndRef: React.RefObject<HTMLDivElement>;
  onClose: () => void;
  onSend: () => void;
  onApplyCohort: (docIds: string[]) => void;
  onStartNewChat: () => void;
  onLoadConversation: (conv: AiConversation) => void;
  totalFiltered: number;
}
export function AiDrawer({
  aiMessages,
  aiHistory,
  aiInput,
  setAiInput,
  aiLoading,
  aiTab,
  setAiTab,
  aiMessagesEndRef,
  onClose,
  onSend,
  onApplyCohort,
  onStartNewChat,
  onLoadConversation,
  totalFiltered
}: AiDrawerProps) {
  // Auto-scroll to bottom of messages
  useEffect(() => {
    if (aiMessagesEndRef.current) {
      aiMessagesEndRef.current.scrollIntoView({
        behavior: 'smooth'
      });
    }
  }, [aiMessages, aiLoading, aiMessagesEndRef]);
  return <div className="absolute top-0 right-0 bottom-0 w-[480px] bg-navy-light border-l border-navy-lighter flex flex-col shadow-2xl z-40" data-id="element-37" data-mp="38x5u1j5mjm5l">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-navy-lighter shrink-0" data-id="element-38" data-mp="3dyv52ysujzto">
        <div className="flex items-center gap-2" data-id="element-39" data-mp="2h8uklbzig5i6">
          <div className="p-1.5 bg-amber/10 rounded-md" data-id="element-40" data-mp="2l94q6nje4twi">
            <SparklesIcon className="w-4 h-4 text-amber" data-id="element-41" data-mp="2q767b2x9aj5c" />
          </div>
          <h2 className="font-serif text-white font-medium text-lg" data-id="element-42" data-mp="2q5v537jgljgc">Ask AI</h2>
          <span className="text-[10px] text-gray-400 bg-navy px-2 py-0.5 rounded-full ml-2 border border-navy-lighter" data-id="element-43" data-mp="2v72ch5howgmh">
            Asking across {totalFiltered.toLocaleString()} docs
          </span>
        </div>
        <div className="flex items-center gap-1" data-id="element-44" data-mp="ae7a3ho0a106">
          <Tooltip content="New Chat" position="bottom" data-id="element-45" data-mp="3gwr94dv8znj5">
            <button onClick={onStartNewChat} className="p-1.5 text-gray-400 hover:text-white hover:bg-navy rounded transition-colors" data-id="element-46" data-mp="3gzdhugtvk92y">
              <PlusIcon className="w-4 h-4" data-id="element-47" data-mp="3quycftwm672o" />
            </button>
          </Tooltip>
          <Tooltip content="Close" shortcut="Esc" position="bottom" align="end" data-id="element-48" data-mp="3lxf0x1raw8ef">
            <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-white hover:bg-navy rounded transition-colors ml-1" data-id="element-49" data-mp="2y5zt4tgr8yvs">
              <XIcon className="w-4 h-4" data-id="element-50" data-mp="37wm3a85pcx73" />
            </button>
          </Tooltip>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-navy-lighter shrink-0" data-id="element-51" data-mp="335m56zkuk930">
        <button onClick={() => setAiTab('chat')} className={`flex-1 px-3 py-2 text-xs font-medium transition-colors relative ${aiTab === 'chat' ? 'text-white' : 'text-gray-500 hover:text-gray-300'}`} data-id="element-52" data-mp="2fgbekax7nv2l">
          Current Chat
          {aiTab === 'chat' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-amber" data-id="element-53" data-mp="pxcun4xoq9th" />}
        </button>
        <button onClick={() => setAiTab('history')} className={`flex-1 px-3 py-2 text-xs font-medium transition-colors relative ${aiTab === 'history' ? 'text-white' : 'text-gray-500 hover:text-gray-300'}`} data-id="element-54" data-mp="urpcxbnfdf3n">
          History
          {aiTab === 'history' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-amber" data-id="element-55" data-mp="18nuk90n80pam" />}
        </button>
      </div>

      {/* Content */}
      {aiTab === 'chat' ? <>
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-[#0A1520]" data-id="element-56" data-mp="zopo20z4t81m">
            {aiMessages.length === 0 && !aiLoading && <div className="h-full flex flex-col items-center justify-center text-center space-y-4" data-id="element-57" data-mp="3kfg9o69tiu68">
                <div className="w-12 h-12 rounded-full bg-amber/10 flex items-center justify-center" data-id="element-58" data-mp="3ohbbdbvnv9kj">
                  <SparklesIcon className="w-6 h-6 text-amber" data-id="element-59" data-mp="8j26khp8u7mf" />
                </div>
                <div className="space-y-1" data-id="element-60" data-mp="3s3md714wpdw">
                  <p className="text-sm text-white font-medium" data-id="element-61" data-mp="3r20e7ikyihe">Ask anything about your documents</p>
                  <p className="text-xs text-gray-500" data-id="element-62" data-mp="3uywjnzst44d5">Get answers or filter to specific document sets</p>
                </div>
                <div className="flex flex-wrap gap-2 max-w-[320px] justify-center mt-2" data-id="element-63" data-mp="3pwyy2ywxoxh1">
                  {['Which docs mention Amendment 3?', 'What is the earliest contract?', 'Find all emails from Sarah Chen', 'Show privileged communications'].map(prompt => <button key={prompt} onClick={() => setAiInput(prompt)} className="bg-navy border border-navy-lighter rounded-lg px-3 py-2 text-xs text-gray-400 hover:text-white hover:border-gray-600 cursor-pointer transition-colors text-left" data-id="element-64" data-mp="dgva12z5wnj0">
                      {prompt}
                    </button>)}
                </div>
              </div>}

            {aiMessages.map(msg => <div key={msg.id} className={`flex flex-col ${msg.role === 'user' ? 'items-end' : 'items-start'}`} data-id="element-65" data-mp="1moyh0acdq5j7">
                <div className={`max-w-[85%] rounded-xl px-3.5 py-2.5 text-sm leading-relaxed ${msg.role === 'user' ? 'bg-amber text-navy rounded-tr-sm font-medium' : 'bg-navy border border-navy-lighter text-gray-200 rounded-tl-sm'}`} data-id="element-66" data-mp="1wh4axpm6yx11">
                  {msg.content}
                </div>
                <span className="text-[10px] text-gray-500 mt-1.5 px-1" data-id="element-67" data-mp="2aessanh1vm1c">
                  {msg.timestamp}
                </span>

                {msg.resultType === 'cohort' && msg.cohortDocIds && <div className="mt-2 w-full max-w-[85%] bg-navy-light border border-amber/30 rounded-lg p-3 shadow-lg" data-id="element-68" data-mp="g2l5jxevubgn">
                    <div className="flex items-center justify-between mb-3" data-id="element-69" data-mp="1jf33l00rqrzp">
                      <span className="text-xs text-amber font-medium flex items-center gap-1.5" data-id="element-70" data-mp="1efgpyph139qd">
                        <FilterIcon className="w-3.5 h-3.5" data-id="element-71" data-mp="1sf87h06r7894" />
                        {msg.cohortDocIds.length} documents found
                      </span>
                    </div>
                    <button onClick={() => onApplyCohort(msg.cohortDocIds!)} className="w-full py-2 bg-amber/10 hover:bg-amber/20 text-amber text-xs font-semibold rounded-md transition-colors border border-amber/20" data-id="element-72" data-mp="22at6qqkftuf6">
                      Apply as filter
                    </button>
                  </div>}
              </div>)}

            {aiLoading && <div className="flex items-start" data-id="element-73" data-mp="2g39cw08my8kb">
                <div className="bg-navy border border-navy-lighter rounded-xl rounded-tl-sm px-4 py-3.5 flex items-center gap-1.5" data-id="element-74" data-mp="2g4kf3vmfn89b">
                  <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{
              animationDelay: '0ms'
            }} data-id="element-75" data-mp="2zmye60spf2c0" />
                  <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{
              animationDelay: '150ms'
            }} data-id="element-76" data-mp="2upoiw4imvwg9" />
                  <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{
              animationDelay: '300ms'
            }} data-id="element-77" data-mp="2lpic11hfxuq2" />
                </div>
              </div>}
            <div ref={aiMessagesEndRef} className="h-1" data-id="element-78" data-mp="1xzxlnxiyjv6r" />
          </div>

          <div className="p-3 border-t border-navy-lighter shrink-0 bg-navy-light" data-id="element-79" data-mp="1y21qqislf08m">
            <div className="flex gap-2" data-id="element-80" data-mp="220honhq7pt0t">
              <textarea value={aiInput} onChange={e => setAiInput(e.target.value)} onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              onSend();
            }
          }} placeholder="Ask about these documents..." rows={2} className="flex-1 bg-navy border border-navy-lighter rounded-lg px-3 py-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-amber focus:ring-1 focus:ring-amber transition-colors resize-none" data-id="element-81" data-mp="2ihbneoj0lkb1" />
              <button onClick={onSend} disabled={!aiInput.trim() || aiLoading} className="self-end p-2.5 bg-amber hover:bg-amber-hover text-navy rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed" title="Send (Enter)" data-id="element-82" data-mp="1pse54lesznia">
                <SendIcon className="w-4 h-4" data-id="element-83" data-mp="1bupm7j4aqqfv" />
              </button>
            </div>
          </div>
        </> : <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#0A1520]" data-id="element-84" data-mp="3rgr0kuwov09w">
          {aiHistory.length === 0 ? <div className="text-center text-gray-500 text-sm mt-10" data-id="element-85" data-mp="c0q6davgyb5">
              No history yet.
            </div> : aiHistory.map(conv => <div key={conv.id} className="bg-navy border border-navy-lighter rounded-lg p-3.5 hover:border-gray-500 transition-colors cursor-pointer group" onClick={() => onLoadConversation(conv)} data-id="element-86" data-mp="33rzgkqt95o87">
                <div className="flex items-start justify-between mb-2.5" data-id="element-87" data-mp="3dnsp7z3uoxkp">
                  <h3 className="text-sm text-gray-200 font-medium line-clamp-2 pr-4 group-hover:text-amber transition-colors" data-id="element-88" data-mp="3ihdpxywtw5nl">
                    {conv.title}
                  </h3>
                  <span className="text-[10px] text-gray-500 shrink-0" data-id="element-89" data-mp="2pz9hbkopqmt5">
                    {conv.createdAt}
                  </span>
                </div>
                <div className="flex items-center gap-4 text-xs text-gray-400" data-id="element-90" data-mp="2maxk0178nb8d">
                  <span className="flex items-center gap-1.5" data-id="element-91" data-mp="2r9qvxp6vmu6f">
                    <MessageSquareIcon className="w-3.5 h-3.5" data-id="element-92" data-mp="2w7sg6dg1gzjh" />
                    {conv.messages.length} msgs
                  </span>
                  {conv.cohortDocIds && <span className="flex items-center gap-1.5 text-amber/80" data-id="element-93" data-mp="23ilex6p79vuj">
                      <FilterIcon className="w-3.5 h-3.5" data-id="element-94" data-mp="28h68woduzjaj" />
                      {conv.cohortDocIds.length} docs
                    </span>}
                </div>
                {conv.cohortDocIds && <button onClick={e => {
          e.stopPropagation();
          onApplyCohort(conv.cohortDocIds!);
        }} className="mt-3.5 w-full py-1.5 bg-navy-lighter hover:bg-navy-hover text-gray-300 text-xs font-medium rounded transition-colors border border-navy-lighter" data-id="element-95" data-mp="2i77v1amnirmt">
                    Re-apply filter
                  </button>}
              </div>)}
        </div>}
    </div>;
}